// ---------------------------------------------------------------------------
// Domain UI types for the ATS Placements tab
// ---------------------------------------------------------------------------

import type { TranslationKey } from "@/translations"
import type { PreVerificationDecision, PreVerificationState } from "@/api/onboarding-api.types"

export interface InternalCompany {
  id: string
  name: string
}

export interface ClientOrganization {
  id: string
  name: string
}

export interface ProfileType {
  id: number
  code: string
  value: string
  /** Whether a Worker Organization is required for this profile type. When false, the field is hidden and { kind: "none" } is sent to the API. */
  orgRequired: boolean
}

export interface WorkerOrganization {
  id: string
  name: string
}

export type ContactStatus =
  | "Active"
  | "Inactive"
  | "PendingApproval"
  | "PendingChange"
  | "Terminated"

export type ProfileStatus =
  | "Active"
  | "Inactive"
  | "PendingApproval"
  | "PendingChange"
  | "Terminated"

export interface WorkerProfile {
  id: string
  name: string
  email: string
  profileTypeCode: string
  workerOrgId: string | null
  contactId: string
  contactStatus: ContactStatus
  profileStatus: ProfileStatus
  /** Contact-level preferred language pulled from FBO (Contact.CultureId), normalized
   * to one of the supported culture codes (e.g. "en-CA" / "fr-CA"). Null when the
   * FBO contact has no CultureId set — the UI then falls back to the PreOnboard
   * value or the current user's locale, in that order. Optional so existing fixtures
   * and mock data can omit the field; the consumer treats undefined as null. */
  preferredLanguage?: string | null
}

export interface Lob {
  id: number
  code: string
  value: string
}

export interface TemplateV2 {
  id: string
  name: string
  lobCode: string
  clientOrgId: string
}

/**
 * ATS source values -- the raw data the ATS sent for this placement.
 * Used to display "[ATS: value]" inline in the drawer so CS can compare
 * what was sent from ATS vs what they select/override.
 */
export interface AtsSourceValues {
  workerName: string
  workerEmail: string
  clientOrgName: string
  internalCompanyName: string | null
  profileTypeCode: string | null
  workerOrgName: string | null
  /** Human-readable names of the fields the ATS supplies for a NEW worker organization, so the drawer
   * can say what "create new" brings with it rather than leaving the reviewer to assume a bare name.
   * Empty when the tenant maps no supplier data. */
  workerOrgAtsFieldKeys?: TranslationKey[]
  lobCode: string | null
  startDate: string
  endDate: string
  billRate: number | null
  payRate: number | null
  billCurrencyCode: string | null
  payCurrencyCode: string | null
}

export const PlacementStatus = {
  PENDING: "Pending",
} as const
export type PlacementStatus = (typeof PlacementStatus)[keyof typeof PlacementStatus]

export interface Placement {
  id: string
  atsId: string
  internalCompanyId: string | null
  clientOrgId: string | null
  workerId: string | null
  workerContactId: string | null
  workerOrgId: string | null
  workerEmail: string
  lobCode: string | null
  profileTypeCode: string | null
  startDate: string
  endDate: string
  billRate: number | null
  payRate: number | null
  billCurrencyCode: string | null
  payCurrencyCode: string | null
  templateV2Id: string | null
  /** Existing contact's preferred language as returned by the API. Full culture name
   * (e.g. "en-CA"); null/undefined when not set or when no matching contact exists yet.
   * The form-state initializer normalizes this to one of the two supported codes;
   * null/undefined triggers a fallback to the current internal user's locale.
   * Optional so existing fixtures and mock data can omit the field without ceremony. */
  workerPreferredLanguage?: string | null
  dateCreated: string
  status: PlacementStatus
  /** Raw values from ATS, always preserved even after CS edits */
  atsSource: AtsSourceValues
  /** The pre-verification linked to the source when it holds up onboarding, as the drawer loaded it. Null/undefined when none. */
  preVerification?: PreVerificationState | null
}

export interface PlacementFormState {
  internalCompanyId: string | null
  clientOrgId: string | null
  /** Email address for the worker -- used when creating a new contact */
  workerEmail: string
  profileTypeCode: string | null
  /** Worker selection -- null until user picks a contact or profiles load for auto-match */
  workerScenario: WorkerScenario | null
  /** Worker org selection -- kind determines which field is sent to the API */
  workerOrgScenario: WorkerOrgScenario | null
  /** Two-letter culture code ("en" / "fr") to apply to a newly created contact. Only sent
   * to the backend when workerScenario.kind === "create-contact" — for existing contacts
   * the preferred language is managed in FBO directly and the value is informational only. */
  workerPreferredCultureCode: string
  startDate: string
  endDate: string
  billRate: string
  payRate: string
  billCurrencyCode: string | null
  payCurrencyCode: string | null
  lobCode: string | null
  templateV2Id: string | null
  /** What the reviewer confirmed about the pre-verification. Null until they confirm. */
  preVerificationDecision: PreVerificationDecision | null
}

export interface PlacementFormErrors {
  internalCompanyId?: string
  clientOrgId?: string
  workerEmail?: string
  profileTypeCode?: string
  worker?: string
  workerOrgId?: string
  lobCode?: string
  startDate?: string
  endDate?: string
  billRate?: string
  payRate?: string
  billCurrencyCode?: string
  payCurrencyCode?: string
  preVerification?: string
}

/** Worker scenario for the drawer -- discriminated union carrying the relevant IDs */
export type WorkerScenario =
  | { kind: "existing-profile"; profileId: string; contactId: string }
  | { kind: "add-profile"; contactId: string }
  | { kind: "create-contact" }

/** Worker org scenario for the drawer -- discriminated union so kind controls what is sent.
 *
 *  - "existing": user picked an existing org from the search.
 *  - "new":      user is creating a new org by name (banner shown until submission).
 *  - "none":     this profile type doesn't require an organization (field is hidden).
 *
 *  Note: the "I don't know organization name" UI mode is tracked as a separate input-mode flag
 *  on the form hook, not as a scenario kind. At submit time it is materialised into a "new"
 *  scenario with a placeholder name (worker name + today's date). */
export type WorkerOrgScenario =
  | { kind: "existing"; organizationId: string }
  | { kind: "new"; organizationName: string }
  | { kind: "none" }
